import React, { useState } from 'react';
import { DEPARTEMENTS_METRO, DEPARTEMENTS_OUTREMER, FRANCE_VIEWBOX } from './franceDepartements';
import './FranceMap.css';

export type DeptState = 'region' | 'isole' | 'libre';

interface FranceMapProps {
  // Etat de couverture d'un departement (par id base) : couvert par une region
  // entiere, selectionne isolement, ou libre.
  deptState: (deptId: number) => DeptState;
  onToggleDept: (deptId: number) => void;
  disabled?: boolean;
  // Sauvegarde en cours : voile clignotant + clic bloque (evite les re-clics qui annulent).
  saving?: boolean;
}

/**
 * Carte de France interactive (departements). Surface d'entree alternative a la
 * liste : chaque departement metropolitain est un path SVG cliquable, colore
 * selon son etat de selection. L'outre-mer est rendu en mini-cartes (geometrie
 * propre a chaque DOM, dans sa boite). Aucune logique de selection ici : tout
 * passe par les callbacks (le parent PerimetreSelector porte la verite via `value`).
 */
const FranceMap: React.FC<FranceMapProps> = ({ deptState, onToggleDept, disabled = false, saving = false }) => {
  const [hover, setHover] = useState<{ nom: string; id: number; x: number; y: number } | null>(null);
  const locked = disabled || saving;

  return (
    <div className={`france-map${saving ? ' is-saving' : ''}`}>
      <div className="france-map-canvas">
        {/* is-locked (pointer-events:none) seulement pendant la sauvegarde : en
            lecture seule on garde le survol (tooltip nom + code), le clic etant
            de toute facon neutralise par le garde !locked sur onClick. */}
        <svg viewBox={FRANCE_VIEWBOX} className={`france-map-svg${saving ? ' is-locked' : ''}`} role="img" aria-label="Carte des departements">
          {DEPARTEMENTS_METRO.map((dep) => {
            const state = deptState(dep.id);
            return (
              <path
                key={dep.id}
                d={dep.d}
                className={`france-dept france-dept--${state}${locked ? ' is-disabled' : ''}`}
                onClick={() => !locked && onToggleDept(dep.id)}
                onMouseMove={(e) => setHover({ nom: dep.nom, id: dep.id, x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setHover(null)}
              />
            );
          })}
        </svg>

        {/* Voile clignotant pendant la sauvegarde : feedback + blocage du clic. */}
        {saving && (
          <div className="france-map-saving">
            <span className="france-map-saving-pill">Enregistrement…</span>
          </div>
        )}

        {hover && !saving && (
          <div className="france-map-tooltip" style={{ left: hover.x + 12, top: hover.y + 12 }}>
            {hover.id} - {hover.nom}
          </div>
        )}
      </div>

      {DEPARTEMENTS_OUTREMER.length > 0 && (
        <div className="france-map-outremer">
          <span className="france-map-outremer-label">Outre-mer</span>
          <div className="france-map-dom-grid">
            {DEPARTEMENTS_OUTREMER.map((d) => {
              const state = deptState(d.id);
              return (
                <button
                  type="button"
                  key={d.id}
                  className={`france-dom-card france-dom-card--${state}`}
                  onClick={() => !locked && onToggleDept(d.id)}
                  disabled={saving}
                  title={`${d.id} - ${d.nom}`}
                >
                  <svg viewBox={d.viewBox} className="france-dom-svg" aria-hidden="true">
                    <path d={d.d} className={`france-dept france-dept--${state}`} />
                  </svg>
                  <span className="france-dom-name">{d.id} - {d.nom}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="france-map-legend">
        <span className="france-legend-item"><span className="france-legend-swatch france-legend-swatch--region" /> Région entière couverte (départements inclus)</span>
        <span className="france-legend-item"><span className="france-legend-swatch france-legend-swatch--isole" /> Département couvert</span>
        <span className="france-legend-item"><span className="france-legend-swatch france-legend-swatch--libre" /> Département non couvert</span>
      </div>
    </div>
  );
};

export default FranceMap;
